/**
 * Multi-Currency Display Utility
 * Shows amounts in both USD and DOP for better user experience
 */

import { API_URL } from './apiConfig';

// Default exchange rate (used as fallback before API responds)
let currentRate = 58.50;
let lastFetched = null;
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes cache

/**
 * Fetch current FX rate from backend
 * @returns {Promise<number>} Current USD to DOP rate
 */
export async function fetchCurrentFxRate() {
  // Return cached rate if fresh
  if (lastFetched && Date.now() - lastFetched < CACHE_DURATION_MS) {
    return currentRate;
  }
  
  try {
    const response = await fetch(`${API_URL}/api/fx/current-rate`);
    if (response.ok) {
      const data = await response.json();
      currentRate = data.rate;
      lastFetched = Date.now();
      return currentRate;
    }
  } catch (error) {
    console.warn('Failed to fetch FX rate, using cached value:', error);
  }
  
  return currentRate;
}

/**
 * Get current FX rate (synchronous, uses cached value)
 * @returns {number} Current USD to DOP rate
 */
export function getCurrentRate() {
  return currentRate;
}

/**
 * Initialize FX rate on app load
 */
export async function initializeFxRate() {
  await fetchCurrentFxRate();
}

/**
 * Format amount for display in both currencies
 * @param {number} usd - Amount in USD
 * @param {number} dop - Amount in DOP (optional, will calculate if not provided)
 * @param {string} primary - Primary currency to show ('usd' or 'dop')
 * @param {number} rate - Exchange rate (optional, uses current rate if not provided)
 * @returns {object} Formatted currency strings
 */
export function formatMultiCurrency(usd, dop = null, primary = 'usd', rate = null) {
  const fxRate = rate || currentRate;
  const dopAmount = dop !== null ? dop : Math.round(usd * fxRate);
  const usdAmount = usd !== null ? usd : (dop / fxRate);
  
  const usdFormatted = `$${usdAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const dopFormatted = `RD$${dopAmount.toLocaleString('es-DO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  
  if (primary === 'dop') {
    return {
      primary: dopFormatted,
      secondary: usdFormatted,
      combined: `${dopFormatted} (≈${usdFormatted})`,
      usd: usdFormatted,
      dop: dopFormatted,
      rawUsd: usdAmount,
      rawDop: dopAmount
    };
  }
  
  return {
    primary: usdFormatted,
    secondary: dopFormatted,
    combined: `${usdFormatted} (≈${dopFormatted})`,
    usd: usdFormatted,
    dop: dopFormatted,
    rawUsd: usdAmount,
    rawDop: dopAmount
  };
}

/**
 * Get preferred currency display based on user role
 * @param {string} role - User role ('sender', 'merchant', 'beneficiary')
 * @returns {string} Preferred primary currency
 */
export function getPreferredCurrency(role) {
  // Supporters (US-based) prefer USD
  // Merchants and Beneficiaries (DR-based) prefer DOP
  if (role === 'sender') {
    return 'usd';
  }
  return 'dop';
}

/**
 * Convert USD to DOP
 * @param {number} usd - Amount in USD
 * @param {number} rate - Exchange rate (optional, uses current rate if not provided)
 * @returns {number} Amount in DOP
 */
export function usdToDop(usd, rate = null) {
  return Math.round(usd * (rate || currentRate));
}

/**
 * Convert DOP to USD
 * @param {number} dop - Amount in DOP
 * @param {number} rate - Exchange rate (optional, uses current rate if not provided)
 * @returns {number} Amount in USD
 */
export function dopToUsd(dop, rate = null) {
  return Math.round((dop / (rate || currentRate)) * 100) / 100;
}

/**
 * Format a single currency amount
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code ('USD' or 'DOP')
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount, currency = 'USD') {
  if (currency === 'DOP') {
    return `RD$${amount.toLocaleString('es-DO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default {
  formatMultiCurrency,
  getPreferredCurrency,
  usdToDop,
  dopToUsd,
  formatCurrency,
  fetchCurrentFxRate,
  getCurrentRate,
  initializeFxRate
};
